<script setup>
import { computed, shallowRef, watch } from 'vue';
import TriStateField from './TriStateField.vue';

const props = defineProps({
  policy: {
    type: Object,
    required: true,
  },
  items: {
    type: Array,
    default: () => [],
  },
  sectionLabels: {
    type: Object,
    required: true,
  },
  states: {
    type: Object,
    required: true,
  },
  compactStates: {
    type: Object,
    default: () => ({}),
  },
  help: {
    type: String,
    required: true,
  },
  effectiveText: {
    type: String,
    required: true,
  },
  lockedText: {
    type: String,
    default: '',
  },
  expandLabel: {
    type: String,
    required: true,
  },
  collapseLabel: {
    type: String,
    required: true,
  },
  loadingLabel: {
    type: String,
    required: true,
  },
  savingKey: {
    type: String,
    default: '',
  },
  targetPrefix: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['change']);

const isExpanded = shallowRef(props.policy.effective !== false);
const canExpand = computed(() => props.policy.effective !== false);
const showChildren = computed(() => canExpand.value && isExpanded.value);
const toggleState = value => {
  if (value === true) {
    return {
      label: props.compactStates.enabled || props.states.enabled,
      fullLabel: props.states.enabled,
      class: 'bg-n-teal-3 text-n-teal-11',
    };
  }

  if (value === false) {
    return {
      label: props.compactStates.disabled || props.states.disabled,
      fullLabel: props.states.disabled,
      class: 'bg-n-ruby-3 text-n-ruby-11',
    };
  }

  return {
    label: props.compactStates.notConfigured || props.states.notConfigured,
    fullLabel: props.states.notConfigured,
    class: 'bg-n-slate-3 text-n-slate-11',
  };
};

const sections = computed(() =>
  ['types', 'receipts']
    .map(key => ({
      key,
      label: props.sectionLabels[key],
      items: props.items
        .filter(item => item.section === key)
        .map(item => ({ ...item, state: toggleState(item.value) })),
    }))
    .filter(section => section.items.length)
);

watch(
  () => props.policy.effective,
  (effective, previous) => {
    if (effective === false) {
      isExpanded.value = false;
    } else if (previous === false) {
      isExpanded.value = true;
    }
  }
);
</script>

<template>
  <div
    class="overflow-hidden border rounded-lg border-n-slate-5"
    :data-target="`${targetPrefix}-policy`"
  >
    <div class="flex flex-col gap-3 p-4">
      <div class="flex items-start justify-between gap-4">
        <div class="flex flex-col gap-1">
          <p class="mb-0 text-sm text-n-slate-11">
            {{ help }}
          </p>
          <div class="flex flex-wrap items-center gap-2">
            <span
              class="px-2 py-0.5 text-xs font-medium rounded-full"
              :class="
                policy.effective
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-ruby-3 text-n-ruby-11'
              "
            >
              {{ effectiveText }}
            </span>
            <span
              v-if="policy.locked && lockedText"
              class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-amber-3 text-n-amber-11"
            >
              {{ lockedText }}
            </span>
          </div>
        </div>

        <button
          v-if="canExpand"
          type="button"
          class="flex items-center gap-1 text-sm text-n-blue-11 hover:text-n-blue-12"
          :aria-expanded="showChildren"
          :data-target="`${targetPrefix}-expand`"
          @click="isExpanded = !isExpanded"
        >
          {{ showChildren ? collapseLabel : expandLabel }}
          <fluent-icon
            :icon="showChildren ? 'chevron-up' : 'chevron-down'"
            size="16"
          />
        </button>
      </div>

      <TriStateField
        :id-prefix="`${targetPrefix}-messages`"
        :value="policy.value"
        :states="states"
        :disabled="policy.locked"
        :loading="savingKey === 'messages'"
        :loading-label="loadingLabel"
        :data-target="`${targetPrefix}-messages`"
        @change="emit('change', 'messages', $event)"
      />
    </div>

    <div
      v-if="showChildren"
      class="flex flex-col gap-5 p-4 border-t bg-n-slate-2 border-n-slate-5"
      :data-target="`${targetPrefix}-children`"
    >
      <div
        v-for="section in sections"
        :key="section.key"
        class="flex flex-col gap-4"
      >
        <p
          class="mb-0 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
        >
          {{ section.label }}
        </p>
        <div class="grid gap-3 md:grid-cols-2">
          <div
            v-for="item in section.items"
            :key="item.key"
            class="overflow-hidden rounded-lg border border-n-slate-5 bg-white dark:bg-n-solid-2"
            :data-target="`${targetPrefix}-${item.key}-options`"
          >
            <div class="flex items-start gap-3 px-3 py-3">
              <div class="min-w-0 flex-1">
                <div class="flex items-start justify-between gap-3">
                  <p
                    class="mb-0 min-w-0 text-sm font-medium leading-5 text-n-slate-12"
                  >
                    {{ item.title }}
                  </p>
                </div>
                <p
                  class="mb-0 mt-1 line-clamp-2 text-xs leading-4 text-n-slate-11"
                >
                  {{ item.help }}
                </p>
                <span
                  class="mt-2 inline-flex max-w-full break-words rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="item.state.class"
                  :title="item.state.fullLabel"
                >
                  {{ item.state.label }}
                </span>
              </div>
            </div>
            <div class="border-t border-n-slate-5 p-3">
              <TriStateField
                :id-prefix="`${targetPrefix}-${item.key}`"
                :value="item.value"
                :states="states"
                :loading="savingKey === item.key"
                :loading-label="loadingLabel"
                :data-target="`${targetPrefix}-${item.key}`"
                @change="emit('change', item.key, $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
